import React, {
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  RadioGroup as RadioGroupRAC,
  Radio as RadioRAC,
  TextField as TextFieldRAC,
  Input as InputRAC,
} from 'react-aria-components';

import { useFocusFieldEvent } from '../../contexts/events-context';
import { useFieldAttributes } from '../../contexts/field-attributes-context';
import { useFieldPropsV2 } from '../../contexts/field-props-context';
import { useTranslation } from '../../contexts/translation-context';
import { findChildOfType, applyInternalProps } from '../../services/find-child';
import { Description } from '../field-description/field-description-headless';
import { Error } from '../field-error/field-error-headless';
import {
  Label,
  type LabelInternalProps,
} from '../field-label/field-label-headless';

import { type RadioGroupFieldProps } from './radio-group-field-props';
import {
  getIsOtherOptionSelected,
  getOtherValue,
  OTHER_ID,
} from './radio-group-field-utils';

export type RadioGroupProps = {
  children: ReactNode;
};

export const RadioGroup = ({ children }: RadioGroupProps) => {
  const {
    id,
    value,
    onChange,
    disabled,
    required,
    onBlur,
    onFocus,
    hasError,
    FieldLayout,
  } = useFieldPropsV2<RadioGroupFieldProps>();

  const { inputId, labelId, ariaDescribedBy } = useFieldAttributes();

  const labelElement = findChildOfType(children, RadioGroup.Label);
  const optionsElement = findChildOfType(children, RadioGroup.Options);
  const descriptionElement = findChildOfType(children, RadioGroup.Description);
  const errorElement = findChildOfType(children, RadioGroup.Error);

  return (
    <FieldLayout
      fieldId={id}
      renderLabel={() =>
        applyInternalProps<LabelInternalProps>(labelElement, { as: 'div' })
      }
      renderInput={() => (
        <RadioGroupRAC
          id={inputId}
          aria-labelledby={labelId}
          aria-describedby={ariaDescribedBy}
          value={value}
          onChange={onChange}
          isDisabled={disabled}
          isRequired={required}
          isInvalid={hasError}
          onBlur={onBlur}
          onFocus={onFocus}
        >
          {optionsElement}
        </RadioGroupRAC>
      )}
      renderDescription={() => (
        <>
          {descriptionElement}
          {errorElement}
        </>
      )}
    />
  );
};

type OptionsProps = {
  children: ReactNode;
  className?: string;
};

const Options = ({ children, className }: OptionsProps) => {
  const { options, addOther, addOtherLabel, id, numberOfColumns, hasError } =
    useFieldPropsV2<RadioGroupFieldProps>();
  const { t } = useTranslation();

  const optionElement = findChildOfType(children, RadioGroup.Option);
  const firstRadioRef = useRef<HTMLLabelElement>(null);

  const otherLabel =
    addOtherLabel || t('add-other.default-other-option-label') || OTHER_ID;

  useFocusFieldEvent(() => {
    firstRadioRef.current?.focus();
  }, id);

  return (
    <div
      className={className}
      data-columns={numberOfColumns}
      data-invalid={hasError}
    >
      {options.map((option, index) =>
        applyInternalProps<OptionInternalProps>(optionElement, {
          key: option.value ?? option.label,
          value: option.value ?? option.label,
          label: option.label,
          index,
          radioRef: index === 0 ? firstRadioRef : undefined,
        }),
      )}
      {addOther &&
        applyInternalProps<OptionInternalProps>(optionElement, {
          key: OTHER_ID,
          value: OTHER_ID,
          label: otherLabel,
          isOther: true,
        })}
    </div>
  );
};

type OptionProps = {
  children: ReactNode;
  className?: string;
};

type OptionInternalProps = {
  value: string;
  label: string;
  index?: number;
  isOther?: boolean;
  radioRef?: React.RefObject<HTMLLabelElement>;
};

const Option = ({ children, className, ...internal }: OptionProps) => {
  const {
    value,
    label,
    isOther = false,
    radioRef,
  } = internal as OptionInternalProps;
  const { value: selectedValue } = useFieldPropsV2<RadioGroupFieldProps>();

  const labelElement = findChildOfType(children, Option.Label);
  const inputElement = findChildOfType(children, Option.Input);
  const otherInputElement = findChildOfType(children, Option.OtherInput);

  const isThisOptionSelected = selectedValue === value;

  const radioClassName = inputElement?.props?.className;

  return (
    <div className={className}>
      <RadioRAC value={value} ref={radioRef} className={radioClassName}>
        {applyInternalProps<OptionLabelInternalProps>(labelElement, { label })}
      </RadioRAC>
      {isOther && isThisOptionSelected && otherInputElement}
    </div>
  );
};

type OptionLabelProps = {
  className?: string;
};

type OptionLabelInternalProps = {
  label: string;
};

const OptionLabel = ({ className, ...internal }: OptionLabelProps) => {
  const { label } = internal as OptionLabelInternalProps;
  return <span className={className}>{label}</span>;
};

type OptionInputProps = {
  className?: string;
};

const OptionInput = (_props: OptionInputProps) => {
  // className is extracted and applied to RadioRAC in the Option component
  return null;
};

type OptionOtherInputProps = {
  className?: string;
};

const OptionOtherInput = ({ className }: OptionOtherInputProps) => {
  const {
    value: fieldValue,
    onChange,
    addOtherLabel,
    addOtherPlaceholder,
    otherInputCharacterLimit,
    disabled,
    options,
  } = useFieldPropsV2<RadioGroupFieldProps>();
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);

  const isOtherOptionSelected = getIsOtherOptionSelected(options, fieldValue);
  const otherLabel =
    addOtherLabel || t('add-other.default-other-option-label') || OTHER_ID;

  const [otherValue, setOtherValue] = useState(
    isOtherOptionSelected && fieldValue
      ? getOtherValue(fieldValue, addOtherLabel)
      : '',
  );

  useEffect(() => {
    if (isOtherOptionSelected && inputRef.current) {
      inputRef.current?.focus();
    }
  }, [isOtherOptionSelected]);

  const handleOtherChange = useCallback(
    (input: string) => {
      let _otherValue;
      if (input) {
        _otherValue = `${addOtherLabel}: ${input}`;
      } else {
        _otherValue = otherLabel;
      }
      onChange(_otherValue);
      setOtherValue(input);
    },
    [onChange, addOtherLabel, otherLabel],
  );

  return (
    <TextFieldRAC
      value={otherValue}
      onChange={handleOtherChange}
      isDisabled={disabled}
    >
      <InputRAC
        ref={inputRef}
        className={className}
        placeholder={addOtherPlaceholder}
        maxLength={otherInputCharacterLimit}
        aria-label={otherLabel}
      />
    </TextFieldRAC>
  );
};

Option.Label = OptionLabel;
Option.Input = OptionInput;
Option.OtherInput = OptionOtherInput;

RadioGroup.Label = Label;
RadioGroup.Options = Options;
RadioGroup.Option = Option;
RadioGroup.Description = Description;
RadioGroup.Error = Error;
